
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  signal,
  SimpleChanges,
} from '@angular/core'
import {
  PalantirButtonComponent,
  PalantirDialogService,
  PalantirIconComponent,
  PalantirSkeletonComponent,
  PalantirTextComponent,
} from '@fieldcontrol/palantir'
import { TranslateModule } from '@ngx-translate/core'
import { ProductsSearchDialogComponent } from './components/products-search-dialog/products-search-dialog.component'
import { MaintenanceProductItem } from './maintenance-products-tab.type'
import { MaintenanceProductsTabService } from './services/maintenance-products-tab.service'
import { MaintenanceProductsTabStateService } from './services/maintenance-products-tab.state.service'

@Component({
  selector: 'maintenance-products-tab',
  templateUrl: './maintenance-products-tab.component.html',
  styleUrl: './maintenance-products-tab.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    PalantirButtonComponent,
    PalantirIconComponent,
    PalantirSkeletonComponent,
    PalantirTextComponent,
    TranslateModule
],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class MaintenanceProductsTabComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public readonly readonly: boolean = false
  @Input() public readonly maintenanceId?: string
  @Input() public readonly companyId?: string
  @Input() public readonly hasPermission: boolean = false

  public readonly pageSize = 5

  public isLoading = signal<boolean>(false)
  public selectedItems = signal<MaintenanceProductItem[]>([])
  public currentPage = signal<number>(1)

  public readonly paginatedItems = computed(this.computePaginatedItems.bind(this))
  public readonly hasPreviousPage = computed(this.computeHasPreviousPage.bind(this))
  public readonly hasNextPage = computed(this.computeHasNextPage.bind(this))
  public readonly paginationFrom = computed(this.computePaginationFrom.bind(this))
  public readonly paginationTo = computed(this.computePaginationTo.bind(this))

  private readonly palantirDialogService = inject(PalantirDialogService)
  private readonly tabService = inject(MaintenanceProductsTabService)
  private readonly stateService = inject(MaintenanceProductsTabStateService)

  private readonly resetBound = this.reset.bind(this)

  public async ngOnInit(): Promise<void> {
    await this.init()
    window.addEventListener('clearMaintenanceProductsVariables', this.resetBound)
  }

  public async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['maintenanceId'] || changes['readonly']) {
      await this.init()
    }
  }

  private async init(): Promise<void> {
    const savedItems = this.stateService.items()
    if (savedItems.length) {
      this.selectedItems.set(savedItems)
    } else if (this.readonly && this.maintenanceId) {
      await this.loadLinkedProducts()
    }
  }

  public ngOnDestroy(): void {
    window.removeEventListener('clearMaintenanceProductsVariables', this.resetBound)
  }

  public async openSearchDialog(): Promise<void> {
    const dialogRef = this.palantirDialogService.openDialog(ProductsSearchDialogComponent, {
      size: 'medium',
      data: { selectedItems: this.selectedItems() },
    })

    const result = await dialogRef.afterClosed<{ selectedItems: MaintenanceProductItem[] }>()

    if (result?.selectedItems !== undefined) {
      this.selectedItems.set(result.selectedItems)
      this.stateService.setItems(result.selectedItems)
      this.currentPage.set(1)
      this.emitSelectedIds()
    }
  }

  public removeItem(id: string): void {
    const newItems = this.selectedItems().filter((item) => item.id !== id)
    this.selectedItems.set(newItems)
    this.stateService.setItems(newItems)
    this.currentPage.set(1)
    this.emitSelectedIds()
  }

  public toPreviousPage(): void {
    if (this.hasPreviousPage()) this.currentPage.set(this.currentPage() - 1)
  }

  public toNextPage(): void {
    if (this.hasNextPage()) this.currentPage.set(this.currentPage() + 1)
  }

  private emitSelectedIds(): void {
    const ids = this.selectedItems().map((item) => item.id)
    window.dispatchEvent(new CustomEvent('maintenanceProductsUpdated', { detail: { productsIds: ids } }))
  }

  private async loadLinkedProducts(): Promise<void> {
    if (!this.maintenanceId) return
    this.isLoading.set(true)
    try {
      const items = await this.tabService.loadLinkedProducts(this.maintenanceId)
      this.selectedItems.set(items)
      this.stateService.setItems(items)
      this.currentPage.set(1)
    } finally {
      this.isLoading.set(false)
    }
  }

  private reset(): void {
    this.selectedItems.set([])
    this.currentPage.set(1)
  }

  private computePaginatedItems(): MaintenanceProductItem[] {
    const start = (this.currentPage() - 1) * this.pageSize
    return this.selectedItems().slice(start, start + this.pageSize)
  }

  private computeHasPreviousPage(): boolean {
    return this.currentPage() > 1
  }

  private computeHasNextPage(): boolean {
    return this.currentPage() * this.pageSize < this.selectedItems().length
  }

  private computePaginationFrom(): number {
    return (this.currentPage() - 1) * this.pageSize + 1
  }

  private computePaginationTo(): number {
    const to = this.currentPage() * this.pageSize
    return to <= this.selectedItems().length ? to : this.selectedItems().length
  }
}
